/**
 * Google Analytics on the public "Start for free" page, and nowhere else.
 *
 * ardealy.com sends a visitor here from its website field or a "Start free"
 * link. To know which ads and pages bring real accounts, this page joins that
 * visit (the `_gl` linker parameter Google adds on the way out) and reports one
 * event when the account exists: `account_created`. Google Ads imports it from
 * Analytics as the conversion campaigns bid on.
 *
 * Consent follows the choice the visitor made on ardealy.com, which passes
 * `consent=granted` along with the website. Without it, Google's consent mode
 * stays denied: no cookies are set, and only cookieless signals are sent.
 *
 * It runs on the production host only, so local and dev signups never count,
 * and never inside the signed-in app.
 */

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-QTZX8W8B2N';
const PRODUCTION_HOSTS = ['pro.ardealy.com'];
const CONSENT_KEY = 'ardealy.signup.consent';

type Gtag = (...args: unknown[]) => void;
type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: Gtag };

let started = false;

const gtag: Gtag = (...args) => {
    const w = window as GtagWindow;
    if (typeof w.gtag === 'function') w.gtag(...args);
};

/** Whether this browser is on a host where signups should be measured. */
export function measured(host = typeof window !== 'undefined' ? window.location.hostname : ''): boolean {
    return PRODUCTION_HOSTS.includes(host);
}

/** The consent the visitor gave on ardealy.com, kept for the second step of the form. */
export function readConsent(search: string): boolean {
    const fromUrl = new URLSearchParams(search || '').get('consent');
    try {
        if (fromUrl === 'granted') sessionStorage.setItem(CONSENT_KEY, 'granted');
        if (fromUrl === 'denied') sessionStorage.removeItem(CONSENT_KEY);
        return sessionStorage.getItem(CONSENT_KEY) === 'granted';
    } catch {
        return fromUrl === 'granted';
    }
}

/** Load Google's tag once, with consent set before anything is sent. */
export function startAnalytics(search: string): void {
    if (started || typeof window === 'undefined' || !measured()) return;
    started = true;
    const granted = readConsent(search) ? 'granted' : 'denied';
    const w = window as GtagWindow;
    w.dataLayer = w.dataLayer || [];
    // gtag must push the arguments object itself, as Google's own snippet does.
    w.gtag = function gtagShim() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer!.push(arguments);
    } as Gtag;
    gtag('consent', 'default', { ad_storage: granted, ad_user_data: granted, ad_personalization: granted, analytics_storage: granted });
    gtag('set', 'ads_data_redaction', granted === 'denied');
    gtag('set', 'url_passthrough', true);
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, { linker: { domains: ['ardealy.com', 'www.ardealy.com'], accept_incoming: true } });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
}

/** The account exists: report it once, and let the event leave before the app takes over. */
export function trackAccountCreated(method: 'email' | 'google'): Promise<void> {
    if (!started) return Promise.resolve();
    return new Promise((resolve) => {
        const done = () => resolve();
        const timer = window.setTimeout(done, 800);
        gtag('event', 'account_created', {
            method,
            transport_type: 'beacon',
            event_callback: () => {
                window.clearTimeout(timer);
                done();
            },
        });
    });
}
